import axios from 'axios'

const TMAP_SCRIPT_URL = 'https://map.qq.com/api/gljs?v=1'
const DEFAULT_COORDINATE_URL = 'https://apis.map.qq.com/ws/coord/v1/translate'
const MAX_BATCH_SIZE = 40
const MAX_REQUEST_URL_LENGTH = 2048
const MAX_CONCURRENT_REQUESTS = 4
const JSONP_TIMEOUT = 15000

let loaderPromise = null
let jsonpSequence = 0
const pendingJsonpRequests = new Set()

function trimTrailingSlash(value) {
  return String(value || '').replace(/\/+$/, '')
}

export function loadTMap() {
  if (window.TMap) return Promise.resolve(window.TMap)
  if (loaderPromise) return loaderPromise

  const key = process.env.VUE_APP_TMAP_KEY
  const serviceHost = trimTrailingSlash(process.env.VUE_APP_TMAP_SERVICE_HOST)
  if (!key && !serviceHost) {
    return Promise.reject(new Error('未配置腾讯地图 Key 或安全代理'))
  }

  if (serviceHost) {
    window._TMapSecurityConfig = { serviceHost }
  }

  loaderPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    const query = key ? `&key=${encodeURIComponent(key)}` : ''
    const removeScript = () => {
      if (script.parentNode) script.parentNode.removeChild(script)
    }
    script.src = `${TMAP_SCRIPT_URL}${query}`
    script.async = true
    script.onload = () => {
      if (window.TMap) {
        resolve(window.TMap)
      } else {
        removeScript()
        loaderPromise = null
        reject(new Error('腾讯地图加载失败'))
      }
    }
    script.onerror = () => {
      removeScript()
      loaderPromise = null
      reject(new Error('腾讯地图加载失败'))
    }
    document.head.appendChild(script)
  })

  return loaderPromise
}

function coordinateLocation(point) {
  const longitude = Number(point.longitude)
  const latitude = Number(point.latitude)
  if (
    !Number.isFinite(longitude) ||
    !Number.isFinite(latitude) ||
    longitude < -180 ||
    longitude > 180 ||
    latitude < -90 ||
    latitude > 90
  ) {
    throw new Error('存在无效的GPS坐标')
  }
  return `${latitude},${longitude}`
}

function coordinateRequestUrl(endpoint, points, key, jsonp) {
  const params = [
    `locations=${encodeURIComponent(points.map(coordinateLocation).join(';'))}`,
    'type=1'
  ]
  if (key) params.push(`key=${encodeURIComponent(key)}`)
  if (jsonp) {
    params.push('output=jsonp')
    params.push('callback=__tmap_coordinate_callback_999999')
  }
  return `${endpoint}?${params.join('&')}`
}

function splitCoordinateBatches(points, endpoint, key, jsonp) {
  const batches = []
  let current = []

  points.forEach((point) => {
    const candidate = current.concat(point)
    const exceedsSize =
      coordinateRequestUrl(endpoint, candidate, key, jsonp).length > MAX_REQUEST_URL_LENGTH
    if (current.length && (candidate.length > MAX_BATCH_SIZE || exceedsSize)) {
      batches.push(current)
      current = [point]
    } else {
      current = candidate
    }
    if (coordinateRequestUrl(endpoint, current, key, jsonp).length > MAX_REQUEST_URL_LENGTH) {
      throw new Error('GPS坐标转换请求过长')
    }
  })

  if (current.length) batches.push(current)
  return batches
}

function conversionResult(response, expectedCount) {
  if (
    !response ||
    Number(response.status) !== 0 ||
    !Array.isArray(response.locations) ||
    response.locations.length !== expectedCount
  ) {
    const status = response && response.status
    const message = (response && response.message) || '返回数据不完整'
    console.error('腾讯坐标转换失败', { status, message })
    throw new Error('GPS坐标转换失败，请稍后重试')
  }
  return response.locations
}

function convertWithJsonp(endpoint, points, key) {
  return new Promise((resolve, reject) => {
    const callbackName = `__tmap_coordinate_callback_${Date.now()}_${jsonpSequence++}`
    const script = document.createElement('script')
    let timer = null

    const cleanup = () => {
      if (timer) window.clearTimeout(timer)
      delete window[callbackName]
      if (script.parentNode) script.parentNode.removeChild(script)
      pendingJsonpRequests.delete(cancel)
    }
    const fail = (message) => {
      cleanup()
      reject(new Error(message))
    }

    window[callbackName] = (response) => {
      cleanup()
      try {
        resolve(conversionResult(response, points.length))
      } catch (error) {
        reject(error)
      }
    }
    const url = coordinateRequestUrl(endpoint, points, key, true)
    script.src = url.replace('__tmap_coordinate_callback_999999', callbackName)
    script.async = true
    script.onerror = () => fail('GPS坐标转换失败，请稍后重试')
    timer = window.setTimeout(() => fail('GPS坐标转换超时，请稍后重试'), JSONP_TIMEOUT)
    const cancel = () => fail('GPS坐标转换已取消')
    pendingJsonpRequests.add(cancel)
    document.head.appendChild(script)
  })
}

export function cancelCoordinateConversions() {
  Array.from(pendingJsonpRequests).forEach((cancel) => cancel())
}

async function convertWithProxy(endpoint, points) {
  try {
    const response = await axios.get(endpoint, {
      params: {
        locations: points.map(coordinateLocation).join(';'),
        type: 1
      },
      timeout: JSONP_TIMEOUT
    })
    return conversionResult(response.data, points.length)
  } catch (error) {
    if (error && error.message && error.message.indexOf('GPS坐标转换') === 0) {
      throw error
    }
    console.error('腾讯坐标转换请求失败', {
      status: error && error.response && error.response.status,
      message: error && error.message
    })
    throw new Error('GPS坐标转换失败，请稍后重试')
  }
}

export async function convertGpsPoints(points) {
  if (!points.length) return []

  const serviceHost = trimTrailingSlash(process.env.VUE_APP_TMAP_SERVICE_HOST)
  const key = process.env.VUE_APP_TMAP_KEY
  const endpoint = trimTrailingSlash(
    process.env.VUE_APP_TMAP_COORDINATE_URL || DEFAULT_COORDINATE_URL
  )
  const useProxy = Boolean(serviceHost)
  if (!useProxy && !key) return Promise.reject(new Error('未配置腾讯地图 Key'))

  const batches = splitCoordinateBatches(points, endpoint, key, !useProxy)
  const results = new Array(batches.length)
  let nextIndex = 0

  async function worker() {
    while (nextIndex < batches.length) {
      const index = nextIndex++
      results[index] = useProxy
        ? await convertWithProxy(endpoint, batches[index])
        : await convertWithJsonp(endpoint, batches[index], key)
    }
  }

  const workerCount = Math.min(MAX_CONCURRENT_REQUESTS, batches.length)
  await Promise.all(Array.from({ length: workerCount }, () => worker()))
  return results.reduce((all, batch) => all.concat(batch), [])
}
